import React, { useState } from "react";
import { Bell, Lock, DollarSign, AlertCircle, ThumbsUp, Mail } from "lucide-react";

interface CommingSoonProps {
  mode: "upvote" | "email";
  heading: string;
  description: string;
  icon?: "bell" | "dollarSign" | "alertCircle"| "lock";
  // Upvote specific props
  initialUpvotes?: number;
  statusLabel?: string;
  // Email specific props
  requestLabel?: string;
  customEmailSubject?: string; // Optional override
}

export default function CommingSoonComponent({
  mode,
  heading,
  description,
  icon = "alertCircle",
  initialUpvotes = 127,
  statusLabel = "Coming soon",
  requestLabel = "Request custom feature",
  customEmailSubject
}: CommingSoonProps) {
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [hasUpvoted, setHasUpvoted] = useState(false);

  const handleUpvote = () => {
    if (!hasUpvoted) {
      setUpvotes((prev) => prev + 1);
      setHasUpvoted(true);
    }
  };

  const formattedHeading = heading.endsWith(" 🚀") ? heading : `${heading} 🚀`;

  // Extract subject from heading by removing any emojis and "Coming Soon" text
  const extractSubjectFromHeading = () => {
    // Remove emojis and common phrases from heading
    let subject = heading
        .replace(/[^\w\s]/g, "")       // Remove emojis/special chars
        .replace(/\b(coming soon|features?)\b/gi, "")  // Remove unwanted phrases
        .replace(/!+/g, "")            // Remove exclamation marks
        .trim()
        .split(/\s+/)                  // Split into words
        .slice(0, 2)                   // Take first two
        .join(" ")                     // Rejoin
        .toLowerCase();                // Lowercase
    
    return subject;
};

  // Format email subject for URL
  const generatedSubject = extractSubjectFromHeading();
  const formattedEmailSubject = `Requesting ${generatedSubject} for Customer Analytics`;
  const emailSubject = customEmailSubject || formattedEmailSubject;
  const encodedEmailSubject = encodeURIComponent(emailSubject);

  // Select icon based on prop
  const IconComponent = {
    bell: Bell,
    dollarSign: DollarSign,
    alertCircle: AlertCircle,
    lock : Lock
  }[icon] || AlertCircle;

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-lg">
      <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
      <div className="relative p-6 flex items-start gap-4">
        <div className="flex-shrink-0 bg-white/20 p-3 rounded-full">
          <IconComponent className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">
            {formattedHeading}
          </h3>
          <p className="text-purple-50 mb-4">
            {description}
          </p>
          
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center text-sm text-purple-50 bg-white/10 px-3 py-1 rounded-full">
              <Lock className="w-4 h-4 mr-1" />
              {statusLabel}
            </span>
            
            {mode === "upvote" ? (
              <button
                onClick={handleUpvote}
                disabled={hasUpvoted}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all
                  ${
                    hasUpvoted
                      ? "bg-white/20 text-white cursor-not-allowed"
                      : "bg-white text-purple-600 hover:bg-purple-50 active:scale-95"
                  }`}
              >
                <ThumbsUp className="w-4 h-4" />
                {hasUpvoted ? "Upvoted" : "Upvote"} ({upvotes})
              </button>
            ) : (
              <a
                href={`mailto:support@example.com?subject=${encodedEmailSubject}`}
                className="inline-flex items-center text-sm text-purple-50 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 mr-1" />
                {requestLabel}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
    </div>
  );
}
